<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import Button from 'dashboard/components-next/button/Button.vue';

defineProps({
  headerTitle: {
    type: String,
    default: '',
  },
  buttonLabel: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['click']);

const route = useRoute();
const { t } = useI18n();

const accountId = computed(() => route.params.accountId);
const campaignTitle = computed(() => t('SIDEBAR.CAMPAIGNS'));
const campaignNavAriaLabel = computed(() => t('SIDEBAR.CAMPAIGNS'));

const campaignNavItems = computed(() => [
  {
    label: 'Live Chat',
    icon: 'i-lucide-message-circle',
    routeName: 'campaigns_livechat_index',
  },
  {
    label: 'SMS',
    icon: 'i-lucide-message-square-text',
    routeName: 'campaigns_sms_index',
  },
  {
    label: 'Email',
    icon: 'i-lucide-mail',
    routeName: 'campaigns_email_index',
  },
  {
    label: 'WhatsApp',
    icon: 'i-lucide-message-circle-more',
    routeName: 'campaigns_whatsapp_index',
  },
]);

const handleButtonClick = () => {
  emit('click');
};
</script>

<template>
  <section
    class="tic-module-layout tic-campaign-shell flex w-full h-full overflow-hidden bg-n-background"
  >
    <aside class="tic-campaign-sidebar">
      <div class="tic-campaign-sidebar__title">
        <span class="i-lucide-megaphone size-5" />
        <span>{{ campaignTitle }}</span>
      </div>
      <nav class="tic-campaign-nav" :aria-label="campaignNavAriaLabel">
        <RouterLink
          v-for="item in campaignNavItems"
          :key="item.routeName"
          :to="{ name: item.routeName, params: { accountId } }"
          class="tic-campaign-nav__item"
          :class="{ 'is-active': route.name === item.routeName }"
        >
          <span :class="item.icon" class="size-4" />
          <span>{{ item.label }}</span>
        </RouterLink>
      </nav>
    </aside>

    <div class="tic-campaign-content">
      <header class="tic-campaign-header">
        <div class="tic-campaign-header__row">
          <span class="text-xl font-medium text-n-slate-12">
            {{ headerTitle }}
          </span>
          <div
            class="tic-campaign-header__action relative group/campaign-button"
          >
            <Button
              :label="buttonLabel"
              icon="i-lucide-plus"
              size="sm"
              class="group-hover/campaign-button:brightness-110"
              @click="handleButtonClick"
            />
            <slot name="action" />
          </div>
        </div>
      </header>
      <main class="tic-campaign-main">
        <slot name="default" />
      </main>
    </div>
  </section>
</template>

<style scoped lang="scss">
.tic-campaign-shell {
  gap: 1.5rem;
  padding: 1.5rem;
}

.tic-campaign-sidebar {
  display: flex;
  flex: 0 0 17.5rem;
  flex-direction: column;
  gap: 1.5rem;
  min-height: 0;
  padding: 1.5rem 1.25rem;
  border: 1px solid rgba(148, 234, 255, 0.16);
  border-radius: 1.25rem;
  background: rgba(9, 16, 31, 0.5);
}

.tic-campaign-sidebar__title {
  display: inline-flex;
  align-items: center;
  gap: 0.65rem;
  color: rgba(248, 250, 252, 0.96);
  font-size: 1rem;
  font-weight: 800;
}

.tic-campaign-nav {
  position: relative;
  display: grid;
  gap: 0.55rem;
  padding-left: 1.25rem;

  &::before {
    position: absolute;
    top: 0.35rem;
    bottom: 0.35rem;
    left: 0.35rem;
    width: 1px;
    background: rgba(148, 234, 255, 0.16);
    content: '';
  }
}

.tic-campaign-nav__item {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  min-height: 2.5rem;
  padding: 0 0.95rem;
  border: 1px solid transparent;
  border-radius: 0.8rem;
  color: rgba(191, 219, 254, 0.86);
  font-size: 0.875rem;
  font-weight: 800;
  text-decoration: none;

  &::before {
    position: absolute;
    left: -0.9rem;
    width: 0.75rem;
    height: 1px;
    background: rgba(148, 234, 255, 0.16);
    content: '';
  }

  &:hover,
  &.is-active {
    background: rgba(255, 255, 255, 0.1);
    color: #ffffff;
  }
}

.tic-campaign-content {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  padding: 1.75rem 1.5rem;
  border: 1px solid rgba(148, 234, 255, 0.16);
  border-radius: 1.25rem;
  background: rgba(9, 16, 31, 0.5);
}

.tic-campaign-header {
  position: relative;
  z-index: 10;
  flex-shrink: 0;
  padding-bottom: 1.5rem;
}

.tic-campaign-header__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  width: 100%;
}

.tic-campaign-main {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}

:global(body:not(.dark)) {
  .tic-campaign-sidebar,
  .tic-campaign-content {
    border-color: rgba(15, 23, 42, 0.08);
    background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.98),
        rgba(246, 253, 255, 0.94)
      ),
      #ffffff;
    box-shadow: 0 12px 32px rgba(31, 38, 135, 0.08);
  }

  .tic-campaign-sidebar__title {
    color: #0f172a;
  }

  .tic-campaign-nav::before,
  .tic-campaign-nav__item::before {
    background: rgba(15, 23, 42, 0.12);
  }

  .tic-campaign-nav__item {
    color: #475569;

    &:hover,
    &.is-active {
      background: rgba(139, 42, 201, 0.1);
      color: #111827;
      box-shadow: none;
    }
  }

  .tic-campaign-main {
    background: transparent;
    color: #111827;
  }
}

@media (max-width: 900px) {
  .tic-campaign-shell {
    flex-direction: column;
    gap: 1rem;
    padding: 1rem;
  }

  .tic-campaign-sidebar {
    flex: 0 0 auto;
    gap: 1rem;
    padding: 1rem;
  }

  .tic-campaign-nav {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.5rem;
    overflow: visible;
    padding-left: 0;

    &::before {
      display: none;
    }
  }

  .tic-campaign-nav__item::before {
    display: none;
  }
}

@media (max-width: 640px) {
  .tic-campaign-shell {
    gap: 0.75rem;
    padding: 0.75rem;
    overflow-y: auto;
  }

  .tic-campaign-sidebar {
    padding: 0.75rem;
  }

  .tic-campaign-nav__item {
    justify-content: center;
    min-width: 0;
    padding-inline: 0.5rem;
    white-space: nowrap;
  }

  .tic-campaign-content {
    flex: 0 0 auto;
    min-height: 24rem;
    padding: 1rem;
  }

  .tic-campaign-header {
    padding-bottom: 1rem;
  }

  .tic-campaign-header__row {
    align-items: stretch;
    flex-direction: column;
  }

  .tic-campaign-header__action,
  .tic-campaign-header__action :deep(button) {
    width: 100%;
  }

  .tic-campaign-main {
    overflow: visible;
  }
}
</style>
